import * as React from 'react';
import classNames from 'classnames';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { Button, Text } from '../../atoms';
import { useTabOverflow } from '../../../utils/useTabOverflow';
import { ActionsButton } from '../../molecules';

import styles from './SwitchTab.module.scss';

// Matches ActionsButton's own default `label` — kept as a reference (not an
// inline literal) so the hidden measurer below doesn't trip the
// no-unnecessary-curly-braces rule while still satisfying Button's required
// `children`.
const EMPTY_LABEL = '';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  initialTab?: string;
  className?: string;
  tabs: Array<{
    id: string;
    label: string;
    component: React.ReactNode;
  }>;
}

const SwitchTab: React.FunctionComponent<Props> = ({
  initialTab,
  className,
  style,
  tabs,
}) => {
  const [selectedItem, setSelectedItem] = React.useState<string | undefined>(
    initialTab,
  );

  const { containerRef, visibleCount } = useTabOverflow(tabs);

  const visibleTabs = React.useMemo(() => {
    const selectedIdx = tabs.findIndex((opt) => opt.id === selectedItem);
    if (selectedIdx >= visibleCount) {
      return [tabs[selectedIdx], ...tabs.slice(0, visibleCount - 1)];
    }
    return tabs.slice(0, visibleCount);
  }, [selectedItem, tabs, visibleCount]);

  const hiddenTabs = React.useMemo(() => {
    const selectedIdx = tabs.findIndex((opt) => opt.id === selectedItem);
    if (selectedIdx >= visibleCount) {
      return [
        ...tabs.slice(visibleCount - 1, selectedIdx),
        ...tabs.slice(selectedIdx + 1),
      ];
    }
    return tabs.slice(visibleCount);
  }, [selectedItem, tabs, visibleCount]);

  return (
    <div
      className={classNames(styles.wrapper, className)}
      style={style}
      ref={containerRef}
    >
      <div
        className={classNames(styles['tab-wrapper'], 'button', {
          left: hiddenTabs.length === 0,
        })}
      >
        {/* This are hidden in order to the useTabOverflow to be able to
        calculate the width */}
        {tabs.map((tab) => (
          <Button
            key={`measure-${tab.id}`}
            size="sm"
            variant="link"
            className={classNames(styles.pill, {
              active: selectedItem === tab.id,
            })}
            style={{
              visibility: 'hidden',
              position: 'absolute',
              pointerEvents: 'none',
            }}
            data-tab /* this is essential for the useTabOverflow */
            disabled
          >
            <Text
              as="span"
              size="sm2"
              className={classNames(
                'fw-bold',
                selectedItem === tab.id ? '' : 'text-gray200',
              )}
            >
              {tab.label}
            </Text>
          </Button>
        ))}

        {visibleTabs.map((tab) => (
          <Button
            key={`visible-${tab.id}`}
            size="sm"
            variant="link"
            className={classNames(styles.pill, {
              active: selectedItem === tab.id,
            })}
            onClick={() => setSelectedItem(tab.id)}
          >
            <Text
              as="span"
              size="sm2"
              className={classNames(
                'fw-bold',
                selectedItem === tab.id ? '' : 'text-gray200',
              )}
            >
              {tab.label}
            </Text>
          </Button>
        ))}

        {/* Hidden "more" button measurer — matches the real ActionsButton
        below (no `size` prop there either, so it defaults to "md") so
        useTabOverflow can reserve room for it whenever some tabs don't fit;
        without this the last "visible" tab ends up pushing the real button
        (and everything after it) past the container's edge. */}
        <Button
          style={{
            visibility: 'hidden',
            position: 'absolute',
            pointerEvents: 'none',
          }}
          data-more-button
          size="md"
          variant="outline"
          icon={faEllipsisVertical}
          disabled
        >
          {EMPTY_LABEL}
        </Button>

        {hiddenTabs.length > 0 && (
          <ActionsButton label="" className={styles['more-button']}>
            {(dismiss) =>
              hiddenTabs.map((tab) => (
                <Button
                  key={`hidden-${tab.id}`}
                  size="sm"
                  variant="link"
                  aria-hidden
                  onClick={() => {
                    setSelectedItem(tab.id);
                    dismiss();
                  }}
                >
                  {tab.label}
                </Button>
              ))
            }
          </ActionsButton>
        )}
      </div>

      <div className="w-100">
        {selectedItem && tabs.find((tab) => tab.id === selectedItem)?.component}
      </div>
    </div>
  );
};

export default SwitchTab;
